/**
 * ScrollToTopHandler
 * Shows a scroll-to-top button when user scrolls down 100vh on single pages
 * Handles both regular single pages and lightbox navigation
 */
export default class ScrollToTopHandler {
  private button: HTMLButtonElement | null = null;
  private readonly threshold = window.innerHeight; // 100vh
  private isVisible = false;
  private scrollContainer: HTMLElement | null = null;
  private boundHandleScroll: (() => void) | null = null;

  constructor() {
    this.init();
  }

  private init(): void {
    if (document.body.classList.contains("page-template-page-about")) {
      // Don't add close button on about page
      return;
    }
    this.createButton();
    this.attachToCurrentContainer();

    // Listen for URL changes (lightbox navigation)
    window.addEventListener("popstate", () => this.handleNavigationChange());

    // Listen for lightbox content loads
    window.addEventListener("lightboxContentLoaded", () =>
      this.handleNavigationChange()
    );

    // Use MutationObserver to detect when lightbox content changes
    this.observeLightboxChanges();
  }

  private observeLightboxChanges(): void {
    const observer = new MutationObserver(() => {
      this.handleNavigationChange();
    });

    // Watch for changes to body classes (single vs home)
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  private handleNavigationChange(): void {
    const shouldShow =
      document.body.classList.contains("single") ||
      document.querySelector(".lightbox-article.open") !== null;

    if (shouldShow) {
      this.attachToCurrentContainer();
      this.updateButtonVisibility();
    } else {
      this.detachFromContainer();
      this.hide();
    }
  }

  private createButton(): void {
    if (this.button) return;
    // If
    this.button = document.createElement("button");
    this.button.className = "scroll-to-top";
    this.button.setAttribute("aria-label", "Scroll to top");
    this.button.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="18 15 12 9 6 15"></polyline>
            </svg>
        `;

    document.body.appendChild(this.button);

    // Start hidden
    this.button.style.opacity = "0";
    this.button.style.visibility = "hidden";

    // Click event to scroll to top
    this.button.addEventListener("click", () => this.scrollToTop());
  }

  private attachToCurrentContainer(): void {
    // Detach from old container first
    this.detachFromContainer();

    // Find the current scroll container
    const lightboxArticle = document.querySelector(
      ".lightbox-article.open"
    ) as HTMLElement;
    const itemWrapper = document.querySelector(".item-wrapper") as HTMLElement;

    this.scrollContainer = lightboxArticle || itemWrapper;

    if (this.scrollContainer) {
      this.boundHandleScroll = () => this.handleScroll();
      this.scrollContainer.addEventListener("scroll", this.boundHandleScroll);

      // Check initial scroll position
      this.handleScroll();
    }
  }

  private detachFromContainer(): void {
    if (this.scrollContainer && this.boundHandleScroll) {
      this.scrollContainer.removeEventListener(
        "scroll",
        this.boundHandleScroll
      );
      this.scrollContainer = null;
      this.boundHandleScroll = null;
    }
  }

  private handleScroll(): void {
    if (!this.scrollContainer) return;

    const scrollTop = this.scrollContainer.scrollTop;

    if (scrollTop > this.threshold && !this.isVisible) {
      this.show();
    } else if (scrollTop <= this.threshold && this.isVisible) {
      this.hide();
    }
  }

  private updateButtonVisibility(): void {
    // Force check current scroll position
    this.handleScroll();
  }

  private show(): void {
    if (!this.button) return;

    this.isVisible = true;
    this.button.style.opacity = "1";
    this.button.style.visibility = "visible";
  }

  private hide(): void {
    if (!this.button) return;

    this.isVisible = false;
    this.button.style.opacity = "0";
    this.button.style.visibility = "hidden";
  }

  private scrollToTop(): void {
    if (this.scrollContainer) {
      this.scrollContainer.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }
}
